import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLANS, yearlyPrice, money, YEARLY_DISCOUNT } from "@/lib/plans";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { Coins, Check, BadgeCheck, Plus, Loader2, Settings } from "lucide-react";

export default function Credits() {
  const { user, updateCredits, updateUser } = useAuth();
  const [verifying, setVerifying] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("month");
  const [checkout, setCheckout] = useState({ open: false, mode: "credits", planId: null, interval: "month" });
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPlan = user?.plan || "free";
  const isSubscribed = currentPlan !== "free" && user?.subscription_status !== "canceled";
  const yearly = billingCycle === "year";
  const discountPct = Math.round(YEARLY_DISCOUNT * 100);

  const openSubscribe = (plan) => setCheckout({ open: true, mode: "subscription", planId: plan.id, interval: billingCycle });
  const openBuyCredits = () => setCheckout({ open: true, mode: "credits", planId: null, interval: "month" });

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err));
      setPortalLoading(false);
    }
  };

  // On return from Stripe (?session_id=...), poll status until paid.
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      setVerifying(false);
      toast.message("Still confirming your payment — refresh in a moment.");
      return;
    }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        updateCredits(data.credits);
        updateUser({ plan: data.plan });
        toast.success("Payment confirmed — credits added!");
        setVerifying(false);
        return;
      }
      if (data.status === "expired") {
        setVerifying(false);
        toast.error("Checkout expired. Please try again.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      setVerifying(false);
      toast.error(apiError(err));
    }
  }, [updateCredits, updateUser]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setVerifying(true);
    pollStatus(sessionId);
    searchParams.delete("session_id");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Auto-open the Stripe Customer Portal when arriving from a "check payment status" email link.
  const manageFired = useRef(false);
  useEffect(() => {
    if (manageFired.current || searchParams.get("manage") !== "1" || !user) return;
    manageFired.current = true;
    searchParams.delete("manage");
    setSearchParams(searchParams, { replace: true });
    if (currentPlan !== "free" && user?.subscription_status !== "canceled") {
      openPortal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="space-y-8" data-testid="credits-page">
      {verifying && (
        <div className="border border-blue-500/30 bg-blue-600/10 rounded-md px-4 py-3 flex items-center gap-2 text-sm" data-testid="payment-verifying">
          <Loader2 className="h-4 w-4 animate-spin text-blue-400" /> Confirming your payment…
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Plans &amp; pricing</p>
          <h1 className="font-head text-3xl font-bold tracking-tight">Upgrade your studio</h1>
          <p className="text-zinc-400 mt-1 text-sm">
            You're on the <span className="text-white capitalize font-medium">{currentPlan}</span> plan.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="border border-blue-500/30 bg-blue-600/10 rounded-md px-5 py-3 inline-flex items-center gap-2">
            <Coins className="h-5 w-5 text-blue-400" />
            <span className="font-head text-2xl font-bold" data-testid="current-credits">{user?.credits ?? 0}</span>
            <span className="text-sm text-zinc-400">credits</span>
          </div>
          <button
            onClick={openBuyCredits}
            data-testid="buy-credits-btn"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-3 rounded-md text-sm font-semibold transition-colors"
          >
            <Plus className="h-4 w-4" /> Buy more credits
          </button>
          {isSubscribed && (
            <button
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="manage-subscription-btn"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-3 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />} Manage subscription
            </button>
          )}
        </div>
      </div>

      <div className="border border-white/10 rounded-md bg-[#121212] p-5 text-sm text-zinc-400">
        <span className="text-zinc-300 font-medium">Every plan includes monthly credits</span> that power AI scripts, studio voiceovers &amp; cinematic scene visuals.
      </div>

      <div className="flex justify-center">
        <div className="inline-flex items-center rounded-full border border-white/10 bg-[#121212] p-1" data-testid="billing-cycle-toggle">
          <button
            onClick={() => setBillingCycle("month")}
            data-testid="cycle-month"
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${!yearly ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("year")}
            data-testid="cycle-year"
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${yearly ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-white"}`}
          >
            Yearly
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${yearly ? "bg-white/20" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"}`}>Save {discountPct}%</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const isFree = plan.id === "free";
          const yp = isFree ? 0 : yearlyPrice(plan.price);
          return (
            <div
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`relative border rounded-md p-6 flex flex-col ${plan.popular ? "border-blue-500 bg-blue-600/10" : "border-white/10 bg-[#121212]"}`}
            >
              {plan.popular && (
                <span className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-wider bg-blue-600 px-2 py-0.5 rounded-full font-semibold">
                  Most popular
                </span>
              )}
              <div className="flex items-center gap-2 mb-1">
                <plan.icon className="h-4 w-4 text-blue-400" />
                <p className="font-head text-lg font-semibold">{plan.name}</p>
              </div>
              <p className="text-xs text-zinc-500 mb-4 min-h-[32px]">{plan.tagline}</p>

              <div className="flex items-end gap-2 mb-1">
                <span className="font-head text-4xl font-black" data-testid={`plan-price-${plan.id}`}>
                  {isFree ? "$0" : `$${yearly ? money(yp) : plan.price}`}
                </span>
                {!isFree && <span className="text-zinc-500 mb-1 text-sm">/ {yearly ? "year" : "month"}</span>}
                {plan.oldPrice && !yearly && (
                  <span className="text-sm text-zinc-600 line-through mb-1.5">${plan.oldPrice}</span>
                )}
              </div>
              {!isFree ? (
                <p className="text-xs mb-4 min-h-[16px]" data-testid={`plan-cycle-note-${plan.id}`}>
                  {yearly
                    ? <span className="text-emerald-400">${money(yp / 12)}/mo billed yearly · save {discountPct}%</span>
                    : <span className="text-zinc-500">or ${money(yp)}/yr — save {discountPct}%</span>}
                </p>
              ) : (
                <div className="mb-4 min-h-[16px]" />
              )}

              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((f, i) => (
                  <li key={f} className="text-sm text-zinc-400 flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <button
                  disabled
                  data-testid={`plan-current-${plan.id}`}
                  className="w-full py-2.5 rounded-md font-semibold bg-white/5 border border-white/10 text-zinc-300 inline-flex items-center justify-center gap-2 cursor-default"
                >
                  <BadgeCheck className="h-4 w-4 text-green-400" /> Current plan
                </button>
              ) : isFree ? (
                <button
                  disabled
                  className="w-full py-2.5 rounded-md font-semibold bg-white/5 border border-white/10 text-zinc-500 cursor-default"
                >
                  Included on signup
                </button>
              ) : (
                <button
                  onClick={() => openSubscribe(plan)}
                  data-testid={`subscribe-${plan.id}`}
                  className={`w-full py-2.5 rounded-md font-semibold transition-colors flex items-center justify-center gap-2 ${plan.popular ? "bg-blue-600 hover:bg-blue-500" : "bg-white/5 border border-white/10 hover:bg-white/10"}`}
                >
                  {plan.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-600 text-center">
        Secure checkout by Stripe. You'll be redirected to complete your purchase.
      </p>
      <CheckoutDialog
        open={checkout.open}
        mode={checkout.mode}
        planId={checkout.planId}
        interval={checkout.interval}
        onClose={() => setCheckout((c) => ({ ...c, open: false }))}
      />
    </div>
  );
}


